import styled, { keyframes } from 'styled-components'
import { pxToRem } from 'styles/functions'

const topBubbles = keyframes`
  0% {
    background-position: 5% 90%, 10% 90%, 10% 90%, 15% 90%, 25% 90%, 25% 90%, 40% 90%, 55% 90%, 70% 90%;
  }
  50% {
    background-position: 0 80%, 0 20%, 10% 40%, 20% 0, 30% 30%, 22% 50%, 50% 50%, 65% 20%, 90% 30%;
  }
  100% {
    background-position: 0 70%, 0 10%, 10% 30%, 20% -10%, 30% 20%, 22% 40%, 50% 40%, 65% 10%, 90% 20%;
    background-size: 0 0, 0 0, 0 0, 0 0, 0 0, 0 0;
  }
`

const bottomBubbles = keyframes`
  0% {
    background-position: 10% -10%, 30% 10%, 55% -10%, 70% -10%, 85% -10%, 70% -10%, 70% 0;
  }
  50% {
    background-position: 0 80%, 20% 80%, 45% 60%, 60% 100%, 75% 70%, 95% 60%, 105% 0;
  }
  100% {
    background-position: 0 90%, 20% 90%, 45% 70%, 60% 110%, 75% 80%, 95% 70%, 110% 10%;
    background-size: 0 0, 0 0, 0 0, 0 0, 0 0, 0 0;
  }
`

export const SCExperienceContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
  
  @media (min-width: 768px) {
    z-index: -2;
    background-color: black;
  }
`

export const SCVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
  touch-action: pinch-zoom;
`

interface overlayProps {
  $size: number
}

export const SCOverlayImage = styled.img<overlayProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: ${({ $size }) => `${pxToRem($size)}`};
  max-height: ${({ $size }) => `${pxToRem($size)}`};
  width: auto;
  height: auto;
  pointer-events: none;
  transition: opacity 0.2s ease;

  @media (min-width: 768px) and (orientation: portrait) {
    max-width: 90%;
    max-height: 95%;
  }

  @media (min-width: 768px) and (orientation: landscape) {
    max-width: 70%;
    max-height: 90%;
  }

  -webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent),
    linear-gradient(to bottom, black, black 95%, transparent);
  mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent),
    linear-gradient(to bottom, black, black 95%, transparent);
  -webkit-mask-composite: destination-in;
  mask-composite: intersect;
`

export const SCFiltersContainer = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  overflow-x: auto;
  padding: ${pxToRem(10)};
  gap: ${pxToRem(16)};

  /* Center the buttons when they fit, but keep them all scroll-reachable
     from the left when they overflow. The auto-margin pseudo-elements
     collapse to zero once there is no spare space. */
  &:before,
  &:after {
    content: '';
    margin: auto;
  }

  @media (orientation: landscape) {
    max-width: 90%;
  }
`

interface SCFiltersButtonProps {
  theme: any;
  $isSelected: boolean;
}
export const SCFiltersButton = styled.button<SCFiltersButtonProps>`
  white-space: nowrap;
  padding: ${pxToRem(8)} ${pxToRem(14)};
  border-radius: ${pxToRem(15)};
  border: ${pxToRem(1)} solid
    ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.photobooth_button_color || theme.button_color || '#34b076' : 'rgba(255,255,255,0.4)'};
  color: ${({ $isSelected }) => ($isSelected ? 'white' : 'black')};
  font-weight: 500;
  cursor: pointer;
  font-size: ${pxToRem(14)};
  flex-shrink: 0;
  z-index: 11;
  position: relative;
  transition:
    transform ease-in 0.1s,
    box-shadow ease-in 0.25s;
  box-shadow: ${({ theme, $isSelected }) =>
    $isSelected
      ? `0 ${pxToRem(2)} ${pxToRem(25)} ${theme.photobooth_button_color || theme.button_color || '#34b076'}80`
      : 'none'};

  @media (min-width: 768px) and (orientation: landscape) {
    font-size: ${pxToRem(20)};
  }

  @media (min-width: 768px) and (orientation: portrait) {
    font-size: ${pxToRem(20)};
  }

  &:focus {
    outline: 0;
  }

  &:before,
  &:after {
    position: absolute;
    content: '';
    display: block;
    width: 140%;
    height: 100%;
    left: -20%;
    z-index: -1000;
    transition: all ease-in-out 0.5s;
    background-repeat: no-repeat;
  }

  &:before {
    display: none;
    top: -75%;
    background-image: radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        transparent 20%,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 30%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        transparent 10%,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 15%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      );
    background-size:
      10% 10%,
      20% 20%,
      15% 15%,
      20% 20%,
      18% 18%,
      10% 10%,
      15% 15%,
      10% 10%,
      18% 18%;
  }

  &:after {
    display: none;
    bottom: -75%;
    background-image: radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        transparent 10%,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 15%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      ),
      radial-gradient(
        circle,
        ${({ theme }) => theme.photobooth_button_color || theme.button_color || '#34b076'} 20%,
        transparent 20%
      );
    background-size:
      15% 15%,
      20% 20%,
      18% 18%,
      20% 20%,
      15% 15%,
      10% 10%,
      20% 20%;
  }

  &:active {
    transform: scale(0.9);
    box-shadow: ${({ theme }) =>
      `0 ${pxToRem(2)} ${pxToRem(25)} ${theme.photobooth_button_color || theme.button_color || '#34b076'}33`};
  }

  &.animate:before {
    display: block;
    animation: ${topBubbles} ease-in-out 0.75s forwards;
  }

  &.animate:after {
    display: block;
    animation: ${bottomBubbles} ease-in-out 0.75s forwards;
  }
`

export const SCExperienceStatus = styled.div`
  height: ${pxToRem(20)};
  display: flex;
  align-items: center;
  line-height: ${pxToRem(20)};
  margin-top: ${pxToRem(5)};

  @media (min-width: 768px) and (orientation: landscape) {
    margin-left: 3rem;
  }
  @media (min-width: 768px) and (orientation: portrait) {
    margin-left: 1rem;
  }
`

export const SCExperienceStatusIcon = styled.div`
  width: ${pxToRem(15)};
  height: ${pxToRem(15)};
  background: red;
  border-radius: 100%;
  margin-left: ${pxToRem(10)};
  margin-right: ${pxToRem(5)};
  font-family: ${({ theme }) => theme.title_font || `'Nunito', sans-serif`};
  font-weight: 700;

  @media (min-width: 768px) and (orientation: landscape) {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
  }
  @media (min-width: 768px) and (orientation: portrait) {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
  }
`

export const SCExperienceStatusLabel = styled.div`
  font-family: ${({ theme }) => theme.title_font || `'Nunito', sans-serif`};
  color: white;
  mix-blend-mode: difference;
  font-weight: 700;
  @media (min-width: 768px) and (orientation: landscape) {
    font-size: ${pxToRem(18)};
  }
  @media (min-width: 768px) and (orientation: portrait) {
    font-size: ${pxToRem(18)};
  }
`

export const SCButtonIcon = styled.img`
  position: absolute;
  bottom: ${pxToRem(95)};
  right: ${pxToRem(14.5)};
  width: ${pxToRem(22)};
  height: ${pxToRem(22)};
  z-index: 20;
  color: white;
  mix-blend-mode: difference;

  @media (orientation: landscape) {
    bottom: ${pxToRem(38)};
    right: ${pxToRem(110)};
  }

  @media (min-width: 768px) and (orientation: portrait) {
    bottom: ${pxToRem(125)};
  }
`

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(20px); }
`;

export const SCDownloadToast = styled.div`
  position: fixed;
  width: 90%;
  bottom: ${pxToRem(105)};
  left: ${pxToRem(20)};
  transform: translateX(-50%);
  background: white;
  border-radius: ${pxToRem(12)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  box-shadow: 0 ${pxToRem(2)} ${pxToRem(8)} rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
  font-size: ${pxToRem(16)};
  font-weight: 500;
  animation: ${fadeInOut} 2s ease forwards;
  z-index: 9999;
`

export const SCDownloadToastEmoji = styled.img`
  width: ${pxToRem(50)};
  height: ${pxToRem(50)};
`